import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, FileText, User, Calendar, Shield, Loader2 } from 'lucide-react';

const VerifyPage = () => {
  const { envelopeId } = useParams();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const [isLoading, setIsLoading] = useState(true);
  const [envelope, setEnvelope] = useState(null);
  const [signers, setSigners] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVerificationData();
  }, [envelopeId]);

  const fetchVerificationData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch envelope (public endpoint, no auth required)
      const envelopeResponse = await axios.get(`${API}/public/verify/${envelopeId}`);
      setEnvelope(envelopeResponse.data);

      // Fetch signers
      if (envelopeResponse.data.signers) {
        setSigners(envelopeResponse.data.signers);
      }

    } catch (err) {
      console.error('Verification failed:', err);
      if (err.response?.status === 404) {
        setError('Document not found or verification unavailable');
      } else {
        setError('Failed to verify document. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-16 w-16 text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Verifying document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <XCircle className="h-20 w-20 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact the document sender.
          </p>
        </div>
      </div>
    );
  }

  const isCompleted = envelope?.status === 'completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Verification Badge */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className={`p-8 text-center ${isCompleted ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-indigo-500 to-purple-600'}`}>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4">
              {isCompleted ? (
                <CheckCircle className="h-12 w-12 text-green-500" />
              ) : (
                <Shield className="h-12 w-12 text-indigo-500" />
              )}
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {isCompleted ? 'Document Verified ✓' : 'Document Verification'}
            </h1>
            <p className="text-white text-opacity-90">
              {isCompleted 
                ? 'This document has been successfully signed and sealed'
                : 'This document is pending signatures'}
            </p>
          </div>

          {/* Document Information */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <FileText className="h-6 w-6 text-indigo-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Document Title</p>
                  <p className="font-semibold text-gray-900">{envelope.title}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="h-6 w-6 text-indigo-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Created</p>
                  <p className="font-semibold text-gray-900">{formatDate(envelope.created_at)}</p>
                </div>
              </div>

              {isCompleted && envelope.completed_at && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Completed</p>
                    <p className="font-semibold text-gray-900">{formatDate(envelope.completed_at)}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-indigo-600 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Envelope ID</p>
                  <p className="font-mono text-xs text-gray-700 break-all">{envelope.id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Signers Information */}
        {signers && signers.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <User className="h-6 w-6 text-indigo-600" />
              Signers ({signers.length})
            </h2>
            <div className="space-y-4">
              {signers.map((signer, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <User className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{signer.display_name || signer.name}</p>
                      <p className="text-sm text-gray-500">{signer.email}</p>
                      <p className="text-xs text-gray-400 mt-1">{signer.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {signer.status === 'completed' ? (
                      <>
                        <div className="flex items-center gap-2 text-green-600 mb-1">
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-semibold">Signed</span>
                        </div>
                        {signer.signed_at && (
                          <p className="text-xs text-gray-500">{formatDate(signer.signed_at)}</p>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-yellow-600">
                        <span className="font-semibold">Pending</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Notice */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 text-center">
          <Shield className="h-8 w-8 text-indigo-600 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-2">Secured by Vivyasigns</h3>
          <p className="text-sm text-gray-600 mb-4">
            This document is protected with enterprise-grade encryption and tamper-proof audit trails.
          </p>
          <p className="text-xs text-gray-500">
            Vivyasigns Enterprise E-Signature Platform © 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
